import type {
  AdmissionCase,
  AdmissionsList,
  AdmissionsQuery,
  CandidateAdmissionView,
  CandidateSubmitItemBody,
  CreateAdmissionBody,
} from "@evolua/api-client";

const COMPANY = "aaaaaaaa-aaaa-4aaa-8aaa-aaaaaaaaaaaa";

const DEFAULT_CHECKLIST: AdmissionCase["checklist"] = [
  {
    key: "cpf",
    label: "CPF",
    required: true,
    kind: "FIELD",
    status: "PENDING",
    value: null,
    documentId: null,
    comment: null,
  },
  {
    key: "rg_doc",
    label: "Documento de identidade (RG/CNH)",
    required: true,
    kind: "DOCUMENT",
    status: "PENDING",
    value: null,
    documentId: null,
    comment: null,
  },
  {
    key: "proof_address",
    label: "Comprovante de residência",
    required: true,
    kind: "DOCUMENT",
    status: "PENDING",
    value: null,
    documentId: null,
    comment: null,
  },
  {
    key: "bank_data",
    label: "Dados bancários",
    required: true,
    kind: "FIELD",
    status: "PENDING",
    value: null,
    documentId: null,
    comment: null,
  },
];

const cases: AdmissionCase[] = [
  {
    id: "dddddddd-dddd-4ddd-8ddd-dddddddddd01",
    companyId: COMPANY,
    candidateName: "Pedro Candidato",
    candidateEmail: "pedro.candidato@example.com",
    status: "INVITED",
    inviteToken: "demo-admission-invite-token01",
    inviteExpiresAt: new Date(Date.now() + 7 * 86_400_000).toISOString(),
    invitePath: "/admissao/demo-admission-invite-token01",
    checklistTemplateId: "eeeeeeee-eeee-4eee-8eee-eeeeeeeeee01",
    checklist: DEFAULT_CHECKLIST.map((i) => ({ ...i })),
    documents: [],
    progressPercent: 0,
    createdAt: new Date().toISOString(),
    createdBy: "demo-hr",
    handedOffAt: null,
    erpHandoffRef: null,
  },
];

function refreshStatus(row: AdmissionCase): void {
  if (row.status === "HANDED_OFF" || row.status === "CANCELLED") return;
  const required = row.checklist.filter((i) => i.required);
  const allValidated = required.every((i) => i.status === "VALIDATED");
  const allSubmitted = required.every(
    (i) =>
      i.status === "SUBMITTED" || i.status === "VALIDATED" || i.status === "REJECTED",
  );
  if (allValidated) row.status = "READY_FOR_HANDOFF";
  else if (allSubmitted) row.status = "IN_REVIEW";
  else if (row.checklist.some((i) => i.status !== "PENDING")) row.status = "COLLECTING";
  const done = required.filter((i) => i.status === "VALIDATED").length;
  row.progressPercent =
    required.length === 0 ? 100 : Math.round((done / required.length) * 100);
}

function cloneCase(row: AdmissionCase): AdmissionCase {
  return {
    ...row,
    checklist: row.checklist.map((i) => ({ ...i })),
    documents: row.documents.map((d) => ({ ...d })),
  };
}

export function listDemoHrAdmissions(query: AdmissionsQuery = {}): AdmissionsList {
  let data = cases.map(cloneCase);
  if (query.status) data = data.filter((c) => c.status === query.status);
  if (query.q?.trim()) {
    const q = query.q.trim().toLowerCase();
    data = data.filter(
      (c) =>
        c.candidateName.toLowerCase().includes(q) ||
        c.candidateEmail.toLowerCase().includes(q),
    );
  }
  return { data, total: data.length };
}

export function createDemoHrAdmission(body: CreateAdmissionBody): AdmissionCase {
  const token = `demo-invite-${crypto.randomUUID().slice(0, 8)}`;
  const row: AdmissionCase = {
    id: crypto.randomUUID(),
    companyId: body.companyId,
    candidateName: body.candidateName,
    candidateEmail: body.candidateEmail,
    status: "INVITED",
    inviteToken: token,
    inviteExpiresAt: new Date(Date.now() + 7 * 86_400_000).toISOString(),
    invitePath: `/admissao/${token}`,
    checklistTemplateId:
      body.checklistTemplateId ?? "eeeeeeee-eeee-4eee-8eee-eeeeeeeeee01",
    checklist: DEFAULT_CHECKLIST.map((i) => ({ ...i })),
    documents: [],
    progressPercent: 0,
    createdAt: new Date().toISOString(),
    createdBy: "demo-hr",
    handedOffAt: null,
    erpHandoffRef: null,
  };
  cases.unshift(row);
  return cloneCase(row);
}

export function getDemoHrAdmission(id: string): AdmissionCase {
  const row = cases.find((c) => c.id === id);
  if (!row) throw new Error("ADMISSION_NOT_FOUND");
  return cloneCase(row);
}

export function validateDemoHrAdmissionItem(
  id: string,
  key: string,
  body: { decision: "VALIDATED" | "REJECTED"; comment?: string },
): AdmissionCase {
  const row = cases.find((c) => c.id === id);
  if (!row) throw new Error("ADMISSION_NOT_FOUND");
  const item = row.checklist.find((i) => i.key === key);
  if (!item) throw new Error("ADMISSION_ITEM_NOT_FOUND");
  item.status = body.decision;
  item.comment = body.comment ?? null;
  refreshStatus(row);
  return cloneCase(row);
}

export function handoffDemoHrAdmission(id: string): AdmissionCase {
  const row = cases.find((c) => c.id === id);
  if (!row) throw new Error("ADMISSION_NOT_FOUND");
  const requiredOk = row.checklist
    .filter((i) => i.required)
    .every((i) => i.status === "VALIDATED");
  if (!requiredOk) throw new Error("ADMISSION_NOT_READY");
  row.status = "HANDED_OFF";
  row.handedOffAt = new Date().toISOString();
  row.erpHandoffRef = `mock-erp-admission/${row.id}`;
  row.progressPercent = 100;
  return cloneCase(row);
}

export function getDemoAdmissionInvite(token: string): CandidateAdmissionView {
  const row = cases.find((c) => c.inviteToken === token);
  if (!row) throw new Error("ADMISSION_INVITE_INVALID");
  return {
    candidateName: row.candidateName,
    companyId: row.companyId,
    status: row.status,
    inviteExpiresAt: row.inviteExpiresAt,
    checklist: row.checklist.map((i) => ({ ...i })),
  };
}

export function submitDemoAdmissionInviteItem(
  token: string,
  key: string,
  body: CandidateSubmitItemBody,
): CandidateAdmissionView {
  const row = cases.find((c) => c.inviteToken === token);
  if (!row) throw new Error("ADMISSION_INVITE_INVALID");
  const item = row.checklist.find((i) => i.key === key);
  if (!item) throw new Error("ADMISSION_ITEM_NOT_FOUND");
  if (item.kind === "DOCUMENT") {
    if (!body.content || !body.filename) throw new Error("ADMISSION_DOCUMENT_REQUIRED");
    const docId = crypto.randomUUID();
    row.documents = row.documents.filter((d) => d.itemKey !== key);
    row.documents.push({
      id: docId,
      itemKey: key,
      filename: body.filename,
      contentType: body.contentType ?? "text/plain",
      uploadedAt: new Date().toISOString(),
    });
    item.documentId = docId;
    item.value = body.filename;
  } else {
    item.value = body.value?.trim() || "OK";
  }
  item.status = "SUBMITTED";
  if (row.status === "INVITED") row.status = "COLLECTING";
  refreshStatus(row);
  return getDemoAdmissionInvite(token);
}
